import { Alert, Button, Card, Modal } from "@aman/design-system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, SimpleForm, StatusBadge, type SimpleFormValue } from "../components/common";
import { isRecord, listFromPayload, stringValue, type AnyRecord } from "../types";

export function BackupsPage() {
  const { t } = useTranslation();
  const query = useApiQuery<unknown>("/backups");
  const restoresQuery = useApiQuery<unknown>("/restores");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [message, setMessage] = useState<string>();
  const [restoreFor, setRestoreFor] = useState<AnyRecord>();
  const rows = listFromPayload(query.data);
  const hasActiveJob = rows.some((row) => ["pending", "running"].includes(stringValue(row["status"])));
  const restoreRows = listFromPayload(restoresQuery.data);
  const hasActiveRestore = restoreRows.some((row) => ["pending", "running"].includes(stringValue(row["status"])));

  useEffect(() => {
    if (!hasActiveJob && !hasActiveRestore) return undefined;
    const timer = window.setInterval(() => { void query.reload(); void restoresQuery.reload(); }, 4_000);
    return () => window.clearInterval(timer);
  }, [hasActiveJob, hasActiveRestore, query.reload, restoresQuery.reload]);

  const create = async (form: SimpleFormValue): Promise<void> => {
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      await api.post("/backups", { type: String(form["type"] || "full"), reason: String(form["reason"] || "") });
      setOpen(false); setMessage(t("pages.backups.queued")); await query.reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const download = async (row: AnyRecord): Promise<void> => {
    setBusy(true); setError(undefined);
    try {
      const id = stringValue(row["id"]);
      const blob = await api.get<Blob>(`/backups/${encodeURIComponent(id)}/download`, {
        raw: true, headers: { Accept: "application/gzip" },
      });
      const url = URL.createObjectURL(blob); const anchor = document.createElement("a");
      anchor.href = url; anchor.download = stringValue(row["storageKey"], `aman-backup-${id}.tar.gz`).split("/").pop() ?? `aman-backup-${id}.tar.gz`;
      anchor.click(); window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const restore = async (form: SimpleFormValue): Promise<void> => {
    if (restoreFor === undefined) return;
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      const id = stringValue(restoreFor["id"]);
      await api.post(`/backups/${encodeURIComponent(id)}/restore`, {
        reason: String(form["reason"] || ""), confirmation: String(form["confirmation"] || ""),
      });
      setRestoreFor(undefined); setMessage(t("pages.backups.restoreQueued")); await restoresQuery.reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };

  return <div className="page-stack">
    <PageHeader title={t("pages.backups.title")} subtitle={t("pages.backups.subtitle")}
      actions={<Button type="button" onClick={() => setOpen(true)}>＋ {t("pages.backups.create")}</Button>} />
    <ApiErrorAlert error={query.error ?? restoresQuery.error ?? error} onRetry={() => { void query.reload(); void restoresQuery.reload(); }} />
    {message !== undefined ? <Alert tone="success">{message}</Alert> : null}
    <Alert tone="info">{t("pages.backups.retention")}</Alert>
    <Card bodyClassName="card-body--flush">
      {query.loading && query.data === undefined ? <LoadingTable columns={8} /> : <DataTable rows={rows} columns={[
        { key: "id", label: "ID", render: (row) => <code>{stringValue(row["id"])}</code> },
        { key: "type", label: t("common.type"), render: (row) => t(`pages.backups.${stringValue(row["type"])}`, { defaultValue: stringValue(row["type"]) }) },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "size", label: t("pages.backups.size"), render: (row) => formatBytes(stringValue(row["size"])) },
        { key: "sha256", label: "SHA-256", render: (row) => stringValue(row["sha256"]) === "" ? "—" : <code>{stringValue(row["sha256"]).slice(0, 16)}…</code> },
        { key: "startedAt", label: t("pages.backups.startedAt") },
        { key: "completedAt", label: t("pages.backups.completedAt"), render: (row) => stringValue(row["completedAt"], "—") },
        { key: "errorCode", label: t("pages.backups.error"), render: (row) => stringValue(row["errorCode"], "—") },
      ]} actions={(row) => stringValue(row["status"]) === "completed" ? <>
        <Button type="button" size="small" variant="secondary" busy={busy} onClick={() => void download(row)}>{t("common.download")}</Button>
        {stringValue(row["type"]) === "full" && isRecord(row["metadata"]) && row["metadata"]["restoreFormat"] === "aman-full-v2"
          ? <Button type="button" size="small" variant="danger" busy={busy} onClick={() => setRestoreFor(row)}>{t("pages.backups.restore")}</Button> : null}
      </> : null} />}
    </Card>
    <Card title={t("pages.backups.restoreHistory")} bodyClassName="card-body--flush">
      {restoresQuery.loading && restoresQuery.data === undefined ? <LoadingTable columns={6} /> : <DataTable rows={restoreRows} columns={[
        { key: "id", label: "ID", render: (row) => <code>{stringValue(row["id"])}</code> },
        { key: "backupId", label: t("pages.backups.backupId"), render: (row) => <code>{stringValue(row["backupId"])}</code> },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "reason", label: t("common.reason") }, { key: "createdAt", label: t("common.date") },
        { key: "errorCode", label: t("pages.backups.error"), render: (row) => stringValue(row["errorCode"], "—") },
      ]} />}
    </Card>
    <Modal open={open} title={t("pages.backups.create")} onClose={() => setOpen(false)}><ApiErrorAlert error={error} />
      <SimpleForm initial={{ type: "full" }} fields={[
        { key: "type", label: t("common.type"), type: "select", required: true, options: [
          { value: "full", label: t("pages.backups.full") }, { value: "database", label: t("pages.backups.database") },
          { value: "storage", label: t("pages.backups.storage") },
        ] },
        { key: "reason", label: t("common.reason"), type: "textarea", required: true },
      ]} busy={busy} submitLabel={t("pages.backups.queue")} onSubmit={create} />
    </Modal>
    <Modal open={restoreFor !== undefined} title={t("pages.backups.restore")} onClose={() => { if (!busy) setRestoreFor(undefined); }}>
      <ApiErrorAlert error={error} /><Alert tone="warning">{t("pages.backups.restoreWarning")}</Alert>
      <SimpleForm fields={[
        { key: "reason", label: t("common.reason"), type: "textarea", required: true },
        { key: "confirmation", label: t("pages.backups.confirmation"), required: true,
          placeholder: `RESTORE ${stringValue(restoreFor?.["id"])}`, hint: t("pages.backups.confirmationHint", { value: `RESTORE ${stringValue(restoreFor?.["id"])}` }) },
      ]} busy={busy} submitLabel={t("pages.backups.restoreNow")} onSubmit={restore} />
    </Modal>
  </div>;
}

function formatBytes(value: string): string {
  const bytes = Number(value); if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"]; const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}
